/**
 * TypeEditFormBody — shared form body for CREATE TYPE / CREATE DOMAIN.
 *
 * Engines:
 *   - mssql:    alias / table kinds.
 *   - postgres: enum / composite / domain / range kinds.
 *   - oracle:   object / table-of / varray kinds (with optional OR REPLACE).
 *   - mysql / sqlite: unsupported affordance with documented redirect.
 *
 * The owning popover seeds `initialValues` from the parked statement; the
 * form is the same body whether mounted inline (Variant 1) or in the
 * Designer pane (Variant 2 — future slice).
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitCreateType,
  getCreateTypeUnsupportedTooltip,
  getSupportedKindsForEngine,
  isCreateTypeSupported,
  validateCreateTypeForEngine,
} from '../../edit/edits/createType';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { TypeFormFields } from './TypeFormFields';
import {
  buildUserDefinedTypeDef,
  defaultKindForEngine,
  type TypeFormValues,
} from './typeFormHelpers';
import { FormActionsSlot } from './FormActionsSlot';
import { FormEngineChip } from './FormEngineChip';

export interface TypeEditFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly typeName: QualifiedName;
  readonly initialValues?: TypeFormValues;
  readonly testId: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function TypeEditFormBody(props: TypeEditFormBodyProps) {
  const { editContext, typeName, initialValues, testId, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;
  const supported = isCreateTypeSupported(engine);
  const supportedKinds = getSupportedKindsForEngine(engine);
  const defaultKind = supportedKinds[0] ?? defaultKindForEngine(engine);

  const [values, setValues] = useState<TypeFormValues>({
    name: initialValues?.name ?? typeName.name,
    kind: initialValues?.kind ?? defaultKind,
    enumValues: initialValues?.enumValues ?? '',
    attributes: initialValues?.attributes ?? '',
    baseType: initialValues?.baseType ?? '',
    subtype: initialValues?.subtype ?? '',
    defaultText: initialValues?.defaultText ?? '',
    notNull: initialValues?.notNull ?? false,
    checkExpressions: initialValues?.checkExpressions ?? '',
    orReplace: initialValues?.orReplace ?? false,
    varraySize: initialValues?.varraySize ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!supported) {
    return (
      <div data-testid={testId} className="flex flex-col gap-3">
        <div className="text-xs font-semibold">Edit type</div>
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-unsupported`}
        >
          {getCreateTypeUnsupportedTooltip(engine)}
        </div>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCancel?.()}
            data-testid={`${testId}-close`}
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  const update = <K extends keyof TypeFormValues>(
    key: K,
    value: TypeFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSubmit() {
    setError(null);
    const userType = buildUserDefinedTypeDef(values, engine, typeName);
    const validationError = validateCreateTypeForEngine(engine, { userType });
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    try {
      await commitCreateType(editContext, { userType });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Edit type</div>
        <FormEngineChip engine={engine} />
      </div>

      <TypeFormFields
        engine={engine}
        values={values}
        supportedKinds={supportedKinds}
        onChange={update}
        testId={testId}
      />

      <span className="text-[10px] text-muted-foreground">
        Submitting appends a fresh CREATE TYPE / CREATE DOMAIN statement to
        the buffer. Use Oracle&apos;s OR REPLACE to replace the existing type.
      </span>

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <FormActionsSlot className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Saving…' : 'Create / Replace type'}
        </Button>
      </FormActionsSlot>
    </div>
  );
}
